import type { ReactNode } from 'react'
import { ChevronLeft, cx } from './primitives'

/**
 * The top of every screen: where you came from, what you are looking at, what you can do
 * to it.
 *
 * `back` is separate from `actions` and always renders above the title, on the left.
 * Leaving the way out inside the action row put it in a different place on every screen -
 * first on a quotation, last on a product, in the middle of a row of buttons either way -
 * and a row of same-looking buttons is the worst place to hide the one that leaves the
 * page. Above and to the left, it is in the same place on every screen and it is the
 * first thing on the reading path rather than the last.
 *
 * Actions stay right-aligned and are ordered by consequence, left to right: destructive
 * first, then the things that merely produce something, then the one action the screen
 * exists for. The primary sits at the end, as far from the destructive one as the row
 * allows.
 */
export function PageHeader({
  eyebrow,
  title,
  hint,
  back,
  actions,
}: {
  eyebrow?: string
  title: string
  hint?: string
  back?: { label: string; onClick: () => void }
  actions?: ReactNode
}) {
  return (
    <header className="mb-6">
      {back && (
        <button
          type="button"
          onClick={back.onClick}
          className="-ml-1 mb-2 inline-flex items-center gap-1 rounded px-1 py-0.5 text-[13px] text-muted transition-colors hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
          <ChevronLeft />
          {back.label}
        </button>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          {eyebrow && (
            <p className="mb-1 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-accent">
              {eyebrow}
            </p>
          )}
          <h1 className="text-2xl font-semibold tracking-[-0.02em]">{title}</h1>
        </div>
        {actions && <div className="flex flex-wrap items-center justify-end gap-2">{actions}</div>}
      </div>

      {/* Its own row, at the header's full width.
          It used to sit inside the title's column under a 42rem cap, which wrapped a
          one-line hint onto two with half the header empty beside it. Widening it in place
          was not the fix: the column shares a flex row with the actions, so a hint long
          enough to need the space would have pushed the buttons onto a line of their own.
          Below the row it has the whole width and nothing to fight over. */}
      {hint && <p className="mt-1 text-[13px] text-muted">{hint}</p>}
    </header>
  )
}

export function Tabs<T extends string>({
  tabs,
  active,
  onSelect,
}: {
  tabs: readonly { id: T; label: string; count?: number }[]
  active: T
  onSelect: (id: T) => void
}) {
  return (
    <div role="tablist" className="mb-4 flex gap-1 border-b border-line">
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          onClick={() => onSelect(tab.id)}
          className={cx(
            '-mb-px border-b-2 px-3 py-2 text-sm font-medium transition-colors',
            tab.id === active
              ? 'border-accent text-ink'
              : 'border-transparent text-muted hover:text-ink-2',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-1.5 font-mono text-[11px] text-faint">{tab.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}
